import React, { Fragment } from 'react';
import { get, noop, isEmpty, castArray, includes, some, defaultTo, isNil, size, times, find, head } from 'lodash';
import classnames from 'classnames';

// local dependencies
import { Icon } from 'modules/icon-bank';
import { getLabel } from 'utils/getLabel';
import { DATA_TYPES, BLANK_VALUE_SYMBOL, TABLE_COLUMN, ELIGIBILITY_STATUS } from 'constants/common';
import { textWithPatternMatch } from 'utils/textWithPatternMatch';
import { checkbox as CheckBox } from 'ui/views/generic/checkbox';
import { isVisible } from 'resolvers/getVisibleEntities';


// data cell views
import { simpleSelect as SimpleSelectCellView } from 'modules/table/cellViews/simpleSelect.view';
import { simpleText as SimpleTextCellView } from 'modules/table/cellViews/simpleText.view';
import { deepSelect as DeepSelectCellView } from 'modules/table/cellViews/deepSelect.view';
import { stepProgress as StepProgressCellview } from 'modules/table/cellViews/stepProgress.view';
import { moodSelect as MoodSelectCellview } from 'modules/table/cellViews/moodSelect.view';
import { descriptionText as DescriptionTextCellView } from 'modules/table/cellViews/descriptionText.view';
import { multiLine as MultiLineView } from 'modules/table/cellViews/multiLine.view';

/****************************************/

// group action view
export const TableGroupAction = ( props ) => {

    // check if highlight allowed
    const highlightCondition = get( props.action, 'config.highlight', null );

    const shouldHighlight = ! isNil( highlightCondition ) ? get( props.item.values, `${highlightCondition}`, false ): false;
    const highlightColor = shouldHighlight ? get( props.layoutConfig, `highlight.${ props.action.name }`, null ): null;

    // action item class name
    const widgetClassName = classnames( 'ui-table__body__data__row__primary__cell__actions__item', {
        'ui-table__body__data__row__primary__cell__actions__item--disabled': ! props.isActive
    } );
    
    // get action icon name
    const iconName = get( props.layoutConfig, `actionIcons.${ props.action.name }`, 'click' );
    // return null if action is not active and `hideIfDisabled`: true
    if( ! props.isActive ) {
        return null;
    }

    let actionLabel = getLabel( get( props, 'action.title', {} ) );

    if( actionLabel.startsWith( '${local.' ) ) {
        const matches = actionLabel.substring( size( '${local.' ), actionLabel.lastIndexOf( '}' ) );
        actionLabel = get( props, `item.values.${matches}`, BLANK_VALUE_SYMBOL );
    }

    return (
        <div className={ widgetClassName } onClick={ props.onClick }>
            <div className='ui-table__body__data__row__primary__cell__actions__item__icon'>
                <Icon
                    name={ iconName }
                    width={ 16 }
                    height={ 16 }
                    fill={ shouldHighlight ? highlightColor : THEME.themeColorPrimary2  }
                />
            </div>

            <div className='ui-table__body__data__row__primary__cell__actions__item__title' style={ { color: highlightColor } }>
                { actionLabel }
            </div>
        </div>
    );
};

// set display name
TableGroupAction.displayName = 'table.TableGroupAction';


/**
 * @desc default table row view.
 * @example
 * import { tableRow as TableRow } from './rowViews/defaultRow.view';
 */
export const tableRow = ( props ) => {

    // display secondary row if at least one column value is provided
    const displaySecondaryRow = some( props.columns.secondary, column => {
        return ! isEmpty( get( props, `item.values.${ column.name }`, '' ) );
    } );

    // check if group action column needs to be displayed
    const groupActions = get( props, 'actionsConfig.groupActions.actions', [] );
    const isGroupActionsColumnVisible = isVisible( get( props, 'actionsConfig.groupActions', {} ), props.visibility );
    
    const groupActionsColumnWidth = get( props.layoutConfig, 'columnWidth._groupActions', 100 );

    // check if selection action column needs to be displayed
    const selectionActions =  get( props, 'actionsConfig.selectionActions.actions', [] );
    const isSelectionActionsVisible = isVisible( get( props, 'actionsConfig.selectionActions', {} ), props.visibility );
    const hideSelection = get( props.item, 'flags.hideSelection', false );

    // to preserve the current row id & if selected or not
    const rowId = get( props, 'item.id', '' );
    const isChecked = includes( props.selectedRows, rowId );
    const statushold = props.item.values.accountStatus === "hold";
    const statusinactive = props.item.values.accountStatus === "inactive";
    const holdinactiveClassNameforPrescriptions = classnames( 'ui-table__body__data__row', {
        'ui-table__body__data__rowhold': statushold,
        'ui-table__body__data__rowinactive': statusinactive,
    } );

    const holdinactiveClassNameforPrescriptionsforCell = classnames( 'ui-table__body__data__row__primary__cell__values', {
        'ui-table__body__data__row__primary__cell__valueshold': statushold,
        'ui-table__body__data__row__primary__cell__valuesinactive': statusinactive,
    } );


    const highlightRow = get( props, 'layoutConfig.highlightRow', undefined );
    let applyHightLightRow = false;

    if( highlightRow ) {
        applyHightLightRow = get( props, `item.values.${ highlightRow }`, '' );
    }

    /*----------------------*/

    /**
     * preloader view for individual row
     * for refresh row actions
     */
    if( get( props, 'item.fetching', false ) ) {
        return (
            <div className='ui-table-preloader__body__row'>
                {
                    times( 9 ).map( colIndex => {
                        return (
                            <div key={ colIndex } className='ui-table-preloader__body__row__cell'>
                                <div className='ui-table-preloader__body__row__cell__value' />
                                <div className='ui-table-preloader__body__row__cell__value' />
                            </div>
                        );
                    } )
                }
            </div>
        );
    }

    return (
        <div className={ holdinactiveClassNameforPrescriptions }>
            { props.isLiveFallback ? (
                <span className='ui-table__body__data__row__live-fallback-badge'
                    aria-label='LIVE — retrieved using live member lookup' role='note'>LIVE</span>
            ) : null }
            <div className='ui-table__body__data__row__primary'>

                {/* highlighted rows */}
                { applyHightLightRow && (
                    <div className='ui-table__body__data__row__primary__highlight'></div>
                ) }

                {/* selection actions cells */}
                {
                    ! isEmpty( selectionActions ) && isSelectionActionsVisible ? (
                        <div className='ui-table__body__data__row__primary__checkbox'>

                            { ! hideSelection && (
                                <div className='ui-table__body__data__row__primary__checkbox__icon'>
                                    {
                                        selectionActions.map( ( selectionAction ) => {
                                            return (
                                                <div id={ rowId } key={ rowId } >
                                                    <CheckBox
                                                        key={ selectionAction.name }
                                                        size='xs'
                                                        value={ isChecked }
                                                        id={ rowId }
                                                        onClick={ () => props.onSelect( { item: rowId, isChecked: ! isChecked } ) }
                                                    />
                                                </div>
                                            );
                                        } )
                                    }
                                </div>
                            ) }
                            
                        </div>
                    ) : null
                }

                {/* regular cells */}
                {
                    props.columns.primary.map( column => {

                        // get column width from the `layoutConfig` (default: 100px)
                        const columnWidth = get( props.layoutConfig, `columnWidth.${ column.name }`, 100 );

                        /*---------*/

                        // get icon to be displayed inside cell
                        const iconCriteria = get( props.layoutConfig, `columnIcons.${ column.name }`, null );
                        const shouldDisplayIcon = ! isEmpty( iconCriteria ) && get( props, `item.${ iconCriteria.path }` );
                        let iconName = shouldDisplayIcon ? get( iconCriteria, 'name' ) : null;

                        /*---------*/

                        // check column is included in wildcard search

                        const _searchableColumn = head( props.searchableColumns );

                        let isSearchableColumn = false;

                        // Had to add this check for carrierId
                        // because of mismatch between column.name and search field
                        // Names cannot be changed now, because will be a bigger change from BE side
                        if ( 'carrierId' === _searchableColumn && 'carrier' === column.name ) {
                            
                            isSearchableColumn = true;
                        } else {

                            isSearchableColumn = includes( props.searchableColumns, column.name );
                        }

                        // const isSearchableColumn = includes( props.searchableColumns, column.name );

                        // column value
                        const value = get( props, `item.values.${ column.name }`, '' );

                        // tooltip message
                        const shouldTooltipBeShown = DATA_TYPES.TEXT_WITH_INFO === column.type;
                        let tooltipMessage = '';
                        let tooltipBackgroundColor = null;
                        let tooltipTextColor = null;
                        let addTooltipCustomClass = false;

                        // Refactored tooltip logic : 37929
                        if ( !isEmpty( get( column, 'uiConfig', {} ) ) ) {

                            const shouldTooltipBeVisible = shouldTooltipBeShown && get( props, `visibility.${get( column, 'uiConfig.info.visibility', null )}`, false );

                            iconName = shouldTooltipBeVisible ? iconName : null;
                        
                            tooltipMessage = shouldTooltipBeVisible ? get( props, `item.values.${ getLabel( get( column, 'uiConfig.info.message', {} ) ) }`, '' ) : '';

                            tooltipBackgroundColor = '#ffffff';
                            tooltipTextColor = '#2d2d2d';
                            addTooltipCustomClass = true;

                        } else { // tooltip earlier case

                            tooltipMessage = shouldTooltipBeShown ? getLabel( get( find( column.options, { value } ), 'description', {} ) ) : '';
                        }

                        // check if column is actionable
                        const actionConfig = defaultTo( column.actionConfig, {} );
                        const isActionActive = ! isEmpty( actionConfig ) && get( props, `item.actionsVisibility.columnActions.${ actionConfig.visibility }`, false );

                        // check if cell is editable
                        const isCellEditable = ! isEmpty( actionConfig ) && get( actionConfig, 'editable', false ) && get( props, `item.actionsVisibility.cellActions.${ column.name }`, false );

                        // get max display values
                        const maxDisplayValues = get( column, 'uiConfig.maxDisplayValues' );

                        // text should be red, if eligibility status is inactive or termed
                        const eligibilityStatusArray = [ ELIGIBILITY_STATUS.INACTIVE, ELIGIBILITY_STATUS.TERMED ];
                        const currentEligibilityStatusValue = get( props, `item.values.${ TABLE_COLUMN.ELIGIBILITY_STATUS }` );
                        const shouldTextBeRed = includes( eligibilityStatusArray, currentEligibilityStatusValue );

                        /*---------*/

                         // text should be blue, if eligibility status is hold
                         const eligibilityStatusArrays = [ ELIGIBILITY_STATUS.HOLD ];
                         const currentEligibilityStatusValues = get( props, `item.values.${ TABLE_COLUMN.ELIGIBILITY_STATUS }` );
                         const shouldTextBeBlue = includes( eligibilityStatusArrays, currentEligibilityStatusValues );
 
                         /*---------*/
                        // if value is empty, render blank symbol
                        if( isEmpty( value ) ) {
                            return (
                                <div key={ column.name } style={ { width: columnWidth } } className='ui-table__body__data__row__primary__cell'>
                                    { BLANK_VALUE_SYMBOL }
                                </div>
                            );
                        }

                        /*---------*/

                        // render column display values for multi-line columns
                        if ( get( column, 'uiConfig.multiLine', false ) ) {
                            return (
                                <div key={ column.name } style={ { width: columnWidth } } className='ui-table__body__data__row__primary__cell'>
                                    <MultiLineView
                                        className={ holdinactiveClassNameforPrescriptionsforCell }
                                        options={ column.options }
                                        value={ value }
                                        columnType={ column.type }
                                        maxDisplayValues={ maxDisplayValues }
                                        displayColors={ get( props.layoutConfig, `valueColors.${ column.name }`, {} ) }
                                        isSearchable={ isSearchableColumn }
                                        searchQuery={ props.searchQuery }
                                        isActionable={ isActionActive }
                                        onClick={ isActionActive ?
                                            () => props.onActionClick( { type: 'column', value: column, item: props.item } )
                                        : noop }
                                        shouldTextBeRed ={ shouldTextBeRed }
                                        shouldTextBeBlue ={shouldTextBeBlue}
                                    />
                                </div>
                            );
                        }

                        /*---------*/

                        // render column display values for `single-select` and `multi-select` data types
                        if( includes( [ DATA_TYPES.SINGLE_SELECT, DATA_TYPES.MULTI_SELECT ], column.type ) ) {

                            return (
                                <div key={ column.name } style={ { width: columnWidth } } className='ui-table__body__data__row__primary__cell'>
                                    <SimpleSelectCellView
                                        className={holdinactiveClassNameforPrescriptionsforCell}
                                        options={ column.options }
                                        value={ value }
                                        maxDisplayValues={ maxDisplayValues }
                                        displayColors={ get( props.layoutConfig, `valueColors.${ column.name }`, {} ) }
                                        isSearchable={ isSearchableColumn }
                                        searchQuery={ props.searchQuery }
                                        isActionable={ isActionActive }
                                        onClick={ isActionActive ?
                                            () => props.onActionClick( { type: 'column', value: column, item: props.item } )
                                        : noop }
                                        shouldTextBeRed={ shouldTextBeRed }
                                        shouldTextBeBlue ={shouldTextBeBlue}
                                    />
                                </div>
                            );
                        }

                        /*----*/

                        // render column display values for `deep-select` and `deep-multi-select` data types
                        if( includes( [ DATA_TYPES.DEEP_SELECT, DATA_TYPES.DEEP_MULTI_SELECT ], column.type ) ) {
                            return (
                                <div key={ column.name } style={ { width: columnWidth } } className='ui-table__body__data__row__primary__cell'>
                                    <DeepSelectCellView
                                        className={holdinactiveClassNameforPrescriptionsforCell}
                                        options={ column.options }
                                        value={ value }
                                        displayColors={ get( props.layoutConfig, `valueColors.${ column.name }`, {} ) }
                                        isSearchable={ isSearchableColumn }
                                        searchQuery={ props.searchQuery }
                                    />
                                </div>
                            );
                        }

                        // render column display values for `stepProgress` data types
                        if( includes( [ DATA_TYPES.STEP_PROGRESS ], column.type ) ) {
                            return (
                                <div key={ column.name } style={ { width: columnWidth } } className='ui-table__body__data__row__primary__cell'>
                                    <StepProgressCellview
                                        className={holdinactiveClassNameforPrescriptionsforCell}
                                        options={ column.options }
                                        value={ value }
                                        displayColors={ get( props.layoutConfig, `valueColors.${ column.name }`, {} ) }
                                        isSearchable={ isSearchableColumn }
                                        searchQuery={ props.searchQuery }
                                        columnType={ column.type }
                                        config={ column.config }
                                    />
                                </div>
                            );
                        }

                        // render column display values for `stepProgress` data types
                        if( includes( [ DATA_TYPES.MOOD ], column.type ) ) {
                            return (
                                <div key={ column.name } style={ { width: columnWidth } } className='ui-table__body__data__row__primary__cell'>
                                    <MoodSelectCellview
                                        className={holdinactiveClassNameforPrescriptionsforCell}
                                        options={ column.options }
                                        value={ value }
                                        displayColors={ get( props.layoutConfig, `valueColors.${ column.name }`, {} ) }
                                        isSearchable={ isSearchableColumn }
                                        searchQuery={ props.searchQuery }
                                        columnType={ column.type }
                                        config={ column.config }
                                    />
                                </div>
                            );
                        }

                        // render column display values for `stepProgress` data types
                        if( includes( [ DATA_TYPES.DESCRIPTION ], column.type ) ) {
                            return (
                                <div key={ column.name } style={ { width: columnWidth } } className='ui-table__body__data__row__primary__cell'>
                                    <DescriptionTextCellView
                                        className={holdinactiveClassNameforPrescriptionsforCell}
                                        value={ value }
                                        icon={ iconName }
                                        isSearchable={ isSearchableColumn }
                                        searchQuery={ props.searchQuery }
                                        maxDisplayValues={ maxDisplayValues }
                                        isActionable={ isActionActive }
                                        onClick={ isActionActive ? () => props.onActionClick( { type: 'column', value: column, item: props.item } ) : noop }
                                        columnType={ column.type }
                                        ellipsesLineCount={ column.ellipsesLineCount}
                                        config= { get( column, 'config', {} ) }
                                    />
                                </div>
                            );
                        }

                        /*----*/

                        // render column display values for other` data types
                        return (
                            <div key={ column.name } style={ { width: columnWidth } } className='ui-table__body__data__row__primary__cell'>
                                <SimpleTextCellView
                                    className={holdinactiveClassNameforPrescriptionsforCell}
                                    value={ value }
                                    icon={ iconName }
                                    tooltipMessage={ tooltipMessage }
                                    isSearchable={ isSearchableColumn }
                                    searchQuery={ props.searchQuery }
                                    maxDisplayValues={ maxDisplayValues }
                                    isActionable={ isActionActive }
                                    onClick={ isActionActive ? () => props.onActionClick( { type: 'column', value: column, item: props.item } ) : noop }
                                    columnType={ column.type }
                                    isCellEditable={ isCellEditable }
                                    onEditActionClick={ isCellEditable ? props.onActionClick : noop }
                                    column={ column }
                                    item={ props.item }
                                    shouldTextBeRed={ shouldTextBeRed }
                                    shouldTextBeBlue ={ shouldTextBeBlue }
                                    tooltipBackgroundColor = { tooltipBackgroundColor }
                                    tooltipTextColor = { tooltipTextColor }
                                    addTooltipCustomClass = { addTooltipCustomClass }
                                />
                            </div>
                        );
                    } )
                }

                {/* group actions cells */}
                {
                    ( isGroupActionsColumnVisible && ! isEmpty( groupActions ) ) ? (
                        <div style={ { width: groupActionsColumnWidth } } className='ui-table__body__data__row__primary__cell'>
                            <div className='ui-table__body__data__row__primary__cell__actions'>
                                {
                                    groupActions.map( groupAction => {

                                        // check if action is active
                                        const isActionActive = get( props, `item.actionsVisibility.groupActions.${ groupAction.visibility }`, true );

                                         // to determine the action associate with `groupAction` is selection or not.
                                        const isSelection = get( groupAction, 'config.isSelection', false );

                                        return (
                                            <TableGroupAction
                                                key={ groupAction.name }
                                                action={ groupAction }
                                                isActive={ isActionActive }
                                                onClick = { () => {
                                                    if( isActionActive ) {

                                                        // this enables the selection on click of the action.
                                                        if( isSelection ) {

                                                            props.onSelect(  { item: rowId, isChecked: ! isChecked } );
                                                            
                                                        } else {

                                                            props.onActionClick( { type: 'groupAction', value: groupAction, item: props.item } );
                                                        }

                                                    } else {
                                                        noop;
                                                    }
                                                } }

                                                { ...props }
                                            />
                                        );
                                    } )
                                }
                            </div>
                        </div>
                    ) : null
                }
            </div>

            {/* secondary row */}
            {
                displaySecondaryRow ? (
                    <div className='ui-table__body__data__row__secondary'>
                        {
                            props.columns.secondary.map( column => {

                                // column value
                                const value = get( props, `item.values.${ column.name }`, '' );

                                // return null view if `value` is empty
                                if( isEmpty( value ) ) {
                                    return null;
                                }

                                // check column is included in wildcard search
                                const isSearchableColumn = includes( props.searchableColumns, column.name );

                                // display column title and value
                                return (
                                    <div key={ column.name } className='ui-table__body__data__row__secondary__cell'>
                                        
                                        {/* label */}
                                        <div className='ui-table__body__data__row__secondary__cell__title'>{ getLabel( column.title ) } : </div>

                                        {/* values */}
                                        <div className='ui-table__body__data__row__secondary__cell__values'>
                                            { castArray( value ).map( ( _valItem, index, orgValue ) => {
                                                return (
                                                    <Fragment key={ index }>
                                                        {/* display value */}
                                                        <span
                                                            title={ _valItem }
                                                            dangerouslySetInnerHTML={ { __html: isSearchableColumn ? textWithPatternMatch( {
                                                                value: _valItem,
                                                                pattern: props.searchQuery,
                                                                wildcard: false,
                                                            } ) : _valItem } }
                                                        />

                                                        {/* comma */}
                                                        { index < orgValue.length - 1 ? ', ' : null }
                                                    </Fragment>
                                                );
                                            }  ) }
                                        </div>
                                    </div>
                                );
                            } )
                        }
                    </div>
                ) : null
            }
        </div>
    );
};

// set display name
tableRow.displayName = 'table.tableRow';
